// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { z } from 'zod';
import { INVERSIS_KYC_ANSWER_OPTIONS } from './inversis-id-map';

export const wealthSourceFundsSchema = z
  .object({
    mainIncomeSource: z.string().min(1, 'Main income source is required'),
    estimatedAnnualIncome: z.string().min(1, 'Estimated annual income is required'),
    approximateWealth: z.string().min(1, 'Estimated position is required'),
    sourceOfFunds: z
      .array(z.string().min(1, 'Source of funds is required'))
      .min(1, 'Select at least one source of funds'),
  })
  .check(({ value, issues }) => {
    const data = value;

    if (
      data.mainIncomeSource &&
      !INVERSIS_KYC_ANSWER_OPTIONS.has(`MAIN_INCOME.${data.mainIncomeSource}`)
    ) {
      issues.push({
        code: 'custom',
        input: data.mainIncomeSource,
        path: ['mainIncomeSource'],
        message: 'Invalid main income source option',
      });
    }

    if (
      data.estimatedAnnualIncome &&
      !INVERSIS_KYC_ANSWER_OPTIONS.has(
        `ANNUAL_INCOME.${data.estimatedAnnualIncome}`,
      )
    ) {
      issues.push({
        code: 'custom',
        input: data.estimatedAnnualIncome,
        path: ['estimatedAnnualIncome'],
        message: 'Invalid estimated annual income option',
      });
    }

    if (
      data.approximateWealth &&
      !INVERSIS_KYC_ANSWER_OPTIONS.has(`ESTIMATED_WEALTH.${data.approximateWealth}`)
    ) {
      issues.push({
        code: 'custom',
        input: data.approximateWealth,
        path: ['approximateWealth'],
        message: 'Invalid approximate wealth option',
      });
    }

    for (const [index, source] of data.sourceOfFunds.entries()) {
      const fundsKey = `FUNDS_ORIGIN.${source}`;

      if (!INVERSIS_KYC_ANSWER_OPTIONS.has(fundsKey)) {
        issues.push({
          code: 'custom',
          input: source,
          path: ['sourceOfFunds', index],
          message: 'Invalid source of funds option',
        });
      }
    }
  });

export type WealthSourceFundsFormData = z.infer<typeof wealthSourceFundsSchema>;
